// O PDF do relatório especial — o mesmo desenho dos outros dois documentos.
//
// Reaproveita cores, títulos numerados e o carregador do pdfmake de
// `documentoPdf.ts`. O que é próprio daqui é desenhar os blocos do Markdown:
// parágrafo, lista, tabela, citação. A seção das perguntas sai numa caixa com
// a barra verde, como no relatório de acompanhamento — é a parte que a família
// abre no corredor.
import { formatarData, formatarDataHora, lerMarkdown, textoPuro, type Bloco, type Trecho } from '@medicov1/shared';
import {
  TINTA, MEIO, FRACO, LINHA, OK, FUNDO_CAIXA,
  titulo, comTitulo, baixarPdfDe,
} from './documentoPdf';
import { ehSecaoDePerguntas, seccionar, tituloDaCarta, vaiParaMedico, type RelatorioEspecialDTO } from './relatorioEspecialTipos';
import { identificar } from './identificacao';

/** Margens do pdfmake: [esquerda, cima, direita, baixo]. Nomeadas para não trocar a ordem. */
type Margem = [number, number, number, number];
const margem = (cima: number, direita: number, baixo: number, esquerda: number): Margem =>
  [esquerda, cima, direita, baixo];

const LINK = '#0e6f86';

/** Os trechos de uma linha como "runs" do pdfmake, herdando o estilo do bloco. */
function trechos(t: Trecho[], base: Record<string, unknown> = {}): unknown[] {
  return t.map((x) => ({
    // A Roboto embutida nao tem a seta: virava um quadrado no papel.
    text: x.texto.replace(/→/g, '–'),
    ...base,
    ...(x.negrito ? { bold: true, color: TINTA } : {}),
    ...(x.italico ? { italics: true } : {}),
    ...(x.link ? { link: x.link, color: LINK, decoration: 'underline' } : {}),
  }));
}

const CORPO = { fontSize: 8.4, color: MEIO, lineHeight: 1.35 };

/**
 * `escala` aumenta a letra de tudo: o pedido da família é carta impressa, lida
 * de pé no balcão, e 8,4 pt é letra de relatório, não de carta.
 */
export function bloco(b: Bloco, escala = 1): unknown {
  const CORPO_ = { ...CORPO, fontSize: CORPO.fontSize * escala };
  switch (b.tipo) {
    case 'titulo':
      return { text: trechos(b.texto, { bold: true, color: TINTA }), fontSize: 9 * escala, margin: margem(5, 0, 2, 0) };
    case 'paragrafo':
      return { text: trechos(b.texto), ...CORPO_, margin: margem(0, 0, 5 * escala, 0) };
    case 'lista': {
      const itens = b.itens.map((it) => ({ text: trechos(it), ...CORPO_, margin: margem(0, 0, 2 * escala, 0) }));
      return b.ordenada
        ? { ol: itens, margin: margem(0, 0, 5, 6) }
        : { ul: itens, margin: margem(0, 0, 5, 6) };
    }
    case 'tabela': {
      const colunas = Math.max(b.cabecalho.length, ...b.linhas.map((l) => l.length));
      const celula = (c: Trecho[] | undefined, cabeca: boolean) => ({
        text: c ? trechos(c, cabeca ? { bold: true, color: FRACO } : { color: TINTA }) : '',
        fontSize: (cabeca ? 6.6 : 7.6) * escala, characterSpacing: cabeca ? 0.6 : 0, lineHeight: 1.25,
      });
      const body: unknown[][] = [];
      if (b.cabecalho.length > 0) body.push(Array.from({ length: colunas }, (_, i) => celula(b.cabecalho[i], true)));
      for (const l of b.linhas) body.push(Array.from({ length: colunas }, (_, i) => celula(l[i], false)));
      const temCabeca = b.cabecalho.length > 0;
      return {
        margin: margem(2, 0, 6, 0),
        table: {
          headerRows: temCabeca ? 1 : 0, keepWithHeaderRows: temCabeca ? 1 : 0, dontBreakRows: true,
          widths: Array(colunas).fill('*'), body,
        },
        layout: {
          hLineWidth: (i: number) => (i === 1 && temCabeca ? 0.8 : 0.4),
          vLineWidth: () => 0,
          hLineColor: (i: number) => (i === 1 && temCabeca ? TINTA : LINHA),
          paddingLeft: () => 0, paddingRight: () => 6, paddingTop: () => 3.5, paddingBottom: () => 3.5,
        },
      };
    }
    case 'citacao':
      return {
        margin: margem(2, 0, 6, 0),
        table: {
          widths: [3, '*'],
          body: [[
            { text: '', fillColor: OK },
            {
              stack: b.paragrafos.map((p) => ({ text: trechos(p), ...CORPO, margin: margem(0, 0, 3, 0) })),
              margin: margem(0, 0, 0, 8),
            },
          ]],
        },
        layout: 'noBorders',
      };
    case 'regua':
      return { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: LINHA }], margin: margem(4, 0, 8, 0) };
  }
}

/**
 * Subtítulo nunca fica sozinho no pé da página.
 *
 * O Ilton imprimiu "Os remédios dela" e o subtítulo "Contra a infecção" ficou
 * no fim da página 1, com os três antibióticos na página 2. Cada subtítulo
 * (###) vai num bloco inquebrável junto com o PRIMEIRO item do que vem depois —
 * só o primeiro: a lista inteira presa num bloco só, se passar de uma página, o
 * pdfmake corta. O resto segue, e a numeração de lista ordenada continua.
 */
export function blocosGrudados(bs: Bloco[], escala = 1): unknown[] {
  const saida: unknown[] = [];
  for (let i = 0; i < bs.length; i++) {
    const b = bs[i]!;
    const prox = bs[i + 1];
    if (b.tipo !== 'titulo' || !prox) { saida.push(bloco(b, escala)); continue; }
    i++; // o próximo sai junto com o subtítulo
    if (prox.tipo === 'lista' && prox.itens.length > 1) {
      const primeiro = bloco({ ...prox, itens: prox.itens.slice(0, 1) }, escala) as Record<string, unknown>;
      primeiro.margin = margem(0, 0, 0, 6); // o resto da lista vem colado, sem vão
      saida.push({ unbreakable: true, stack: [bloco(b, escala), primeiro] });
      const resto = bloco({ ...prox, itens: prox.itens.slice(1) }, escala) as Record<string, unknown>;
      if (prox.ordenada) resto.start = 2;
      // Sem a margem de cima: é a mesma lista, continuando.
      saida.push(resto);
    } else if (prox.tipo === 'tabela' && prox.linhas.length > 15) {
      // Tabela grande não cabe presa: o subtítulo fica com o cabeçalho dela,
      // que o pdfmake já repete e segura com a primeira linha.
      saida.push(bloco(b, escala), bloco(prox, escala));
    } else {
      saida.push({ unbreakable: true, stack: [bloco(b, escala), bloco(prox, escala)] });
    }
  }
  return saida;
}

/** Monta a definição do pdfmake. Pura: testável fora do navegador. */
export function montarDocumentoEspecial(dados: RelatorioEspecialDTO): unknown {
  // O que é para entregar à equipe identifica como o hospital; o resto, como a família.
  const quem = identificar(dados.paciente, vaiParaMedico(dados));
  const conteudo: unknown[] = [];
  let n = 0;
  /**
   * Pedido da família: uma CARTA para entregar ao hospital, não uma análise.
   * Sai sem "relatório especial", sem "o que motivou" e sem o aviso de
   * literatura — e o rodapé não fala do sistema, porque o papel é da família.
   */
  const pedido = dados.tipo === 'pedido';

  if (pedido) {
    conteudo.push({
      columns: [
        { width: '*', text: tituloDaCarta(dados.titulo), fontSize: 15, color: TINTA },
        { width: 'auto', text: 'PEDIDO DA FAMÍLIA', fontSize: 9, bold: true, characterSpacing: 1.4, alignment: 'right', color: TINTA, margin: margem(4, 0, 0, 0) },
      ],
      margin: margem(0, 0, 8, 0),
    });
    conteudo.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.2, lineColor: TINTA }], margin: margem(0, 0, 10, 0) });
  }

  // ------------------------------------------------------ identificação
  if (!pedido) conteudo.push({
    columns: [
      {
        width: '*',
        stack: [
          { text: quem.nome, fontSize: 19, color: TINTA, margin: margem(0, 0, 1, 0) },
          // 7,8 (era 8,5): com nome, nascimento, prontuário, atendimento, leito e internação, a linha cabe inteira (05/10).
          { text: quem.linha, fontSize: 7.8, color: FRACO },
        ],
      },
      {
        width: 'auto',
        stack: [
          { text: 'RELATÓRIO ESPECIAL', fontSize: 9, bold: true, characterSpacing: 1.4, alignment: 'right', color: TINTA },
          { text: `análise de ${formatarDataHora(new Date(dados.geradoEm))}`, fontSize: 7.5, color: FRACO, alignment: 'right', margin: margem(2, 0, 0, 0) },
          { text: `registrado por ${dados.autor}`, fontSize: 7.5, color: FRACO, alignment: 'right' },
        ],
      },
    ],
    margin: margem(0, 0, 8, 0),
  });
  if (!pedido) {
    conteudo.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.2, lineColor: TINTA }], margin: margem(0, 0, 10, 0) });
    conteudo.push({ text: dados.titulo, fontSize: 14, color: TINTA, margin: margem(0, 0, 4, 0) });
    conteudo.push({
      text: [{ text: 'O que motivou: ', bold: true, color: TINTA }, dados.assunto],
      fontSize: 8.6, color: MEIO, lineHeight: 1.35, margin: margem(0, 0, 6, 0),
    });
    conteudo.push({
      text: [
        // Sem fontes, não se promete "literatura citada no fim".
        dados.fontes.length > 0
          ? 'Este documento cruza o que a família registrou no sistema com a literatura citada no fim. '
          : 'Este documento reúne o que a família registrou no sistema e os laudos do hospital. ',
        { text: 'Não é diagnóstico', bold: true },
        ': as hipóteses ficam para a avaliação da equipe, e a conduta é dela.',
      ],
      fontSize: 8.4, color: MEIO, lineHeight: 1.35, margin: margem(0, 0, 6, 0),
    });
  }

  // ------------------------------------------------------------- seções
  for (const s of seccionar(lerMarkdown(dados.texto))) {
    const escala = pedido ? 1.25 : 1;
    let blocos = blocosGrudados(s.blocos, escala);
    // A seção que abre com uma lista gruda o título só no PRIMEIRO item, como
    // os subtítulos (blocosGrudados): presa inteira ao título, uma lista de
    // seis itens que não cabe no fim da página empurrava a seção toda e
    // deixava um terço da folha em branco (relatório de 29/09).
    const b0 = s.blocos[0];
    if (s.titulo && !ehSecaoDePerguntas(s) && b0?.tipo === 'lista' && b0.itens.length > 1) {
      const primeiro = bloco({ ...b0, itens: b0.itens.slice(0, 1) }, escala) as Record<string, unknown>;
      primeiro.margin = margem(0, 0, 0, 6);
      const resto = bloco({ ...b0, itens: b0.itens.slice(1) }, escala) as Record<string, unknown>;
      if (b0.ordenada) resto.start = 2;
      blocos = [primeiro, resto, ...blocosGrudados(s.blocos.slice(1), escala)];
    }
    if (!s.titulo) { conteudo.push(...blocos); continue; }

    const corpo = ehSecaoDePerguntas(s)
      ? [{
        margin: margem(4, 0, 6, 0),
        table: {
          widths: [3, '*'],
          body: [[
            { text: '', fillColor: OK },
            { stack: blocos, fillColor: FUNDO_CAIXA, margin: margem(6, 8, 2, 8) },
          ]],
        },
        layout: 'noBorders',
      }]
      : blocos;

    // O Markdown pode vir numerado ("## 3. O metabolico"); o papel numera por
    // conta propria, como os outros documentos. Um numero so.
    const rotulo = textoPuro(s.titulo).replace(/^\d+[.)]\s+/, '');
    conteudo.push(...comTitulo(titulo(++n, rotulo), corpo[0] ?? { text: '' }, corpo.slice(1)));
  }

  // ------------------------------------------------------------- fontes
  if (!pedido && dados.fontes.length > 0) {
    const itens = dados.fontes.map((f) => ({
      stack: [
        { text: f.titulo, fontSize: 7.4, color: TINTA },
        { text: f.url, fontSize: 6.2, color: FRACO, link: f.url },
      ],
      margin: margem(0, 0, 3, 0),
    }));
    // Como as seções: o título vai preso só à primeira fonte, e o resto pode
    // continuar na folha seguinte — a lista inteira presa pulava de página e
    // deixava um vão no fim da anterior.
    conteudo.push(...comTitulo(
      titulo(++n, 'Fontes'),
      { margin: margem(4, 0, 0, 0), ol: itens.slice(0, 1), fontSize: 7.4 },
      itens.length > 1 ? [{ ol: itens.slice(1), start: 2, fontSize: 7.4 }] : [],
    ));
  }

  return {
    pageSize: 'A4',
    pageMargins: [40, 46, 40, 44],
    defaultStyle: { font: 'Roboto', fontSize: 8.5, color: TINTA },
    info: { title: dados.titulo, author: 'saudev1' },
    footer: (pagina: number, total: number) => ({
      columns: [
        { text: pedido ? '' : 'saudev1 · registro de apoio da família · não é o prontuário do hospital', fontSize: 6.4, color: FRACO },
        { text: `${pagina} / ${total}`, fontSize: 6.4, color: FRACO, alignment: 'right' },
      ],
      margin: margem(0, 40, 0, 40),
    }),
    content: conteudo,
  };
}

const semAcento = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export async function gerarPdfEspecial(dados: RelatorioEspecialDTO): Promise<void> {
  const data = formatarData(new Date(dados.geradoEm)).replace(/\//g, '-');
  const prefixo = dados.tipo === 'pedido' ? 'pedido' : 'relatorio-especial';
  await baixarPdfDe(montarDocumentoEspecial(dados), `${prefixo}-${semAcento(dados.titulo)}-${data}.pdf`);
}
